import React, { useState, useEffect, useMemo } from "react";
import SupplierSuggestionList from './SupplierSuggestionList';
import BoxPricingTable from './BoxPricingTable';
import PiecePricingTable from './PiecePricingTable';
import DynamicProductFormFields from "./DynamicProductFormFields";
import {
  fetchDynamicForm,
  isSectionVisible,
  loadProductFormOptionsMap,
  resolveProductFormFieldName,
} from "./dynamicFormApi";
import {
  mergePriceTierVisibleMap,
  resolvePriceTierMapFromField,
} from "../../Settings/productFormSubFieldUtils";
import { resolveProductSettingsFieldName } from "../../Settings/productFormFieldDefaults";
import config from "../../common/Config";
import QuickBooksPushPrompt from "../../common/QuickBooksPushPrompt";
import axios from 'axios';
import { Box, Typography, TextField, Button, Paper, Grid2, Radio, RadioGroup, FormControlLabel, FormControl, Checkbox, IconButton, Autocomplete, Select, MenuItem, Dialog, DialogContent } from '@mui/material';
import { toast } from 'sonner';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { format } from 'date-fns';
import { Iconify } from '../../Minimal/src/components/iconify/iconify';
import { encryptPayload, resolveEncryptedResponse, normalizeEncryptedApiResponse, plainObjectFromFormData } from "../../../utils/customerCrypto";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../../utils/companySettingsCache";
import { postGetPriceVariation } from "../../../utils/priceVariationApi";

const computeCbmFromDimensions = (length, width, height, lengthUnit, widthUnit, heightUnit) => {
  const unitConversionToMM = { mm: 1, cm: 10, in: 25.4 };
  const parseDim = (v) => {
    const n = parseFloat(String(v ?? '').trim().replace(',', '.'));
    return Number.isFinite(n) ? n : NaN;
  };
  const w = parseDim(width);
  const h = parseDim(height);
  const l = parseDim(length);
  if (!Number.isFinite(w) || !Number.isFinite(h) || !Number.isFinite(l)) {
    return '0';
  }
  const lf = unitConversionToMM[lengthUnit || 'mm'] || 1;
  const wf = unitConversionToMM[widthUnit || 'mm'] || 1;
  const hf = unitConversionToMM[heightUnit || 'mm'] || 1;
  const cbm = (l * lf * w * wf * h * hf) / 1e9;
  if (!Number.isFinite(cbm)) return '0';
  if (cbm === 0) return '0';
  const s =
    Math.abs(cbm) < 0.0001
      ? String(parseFloat(cbm.toFixed(6)))
      : String(parseFloat(cbm.toFixed(4)));
  return s;
};

const Edit_Product = ({ productId, open = false, onClose, onSuccess, useDynamicForm = false }) => {
  const accessToken = localStorage.getItem('accessToken');
  const jwtToken = localStorage.getItem('token');
  const company_tax_type = localStorage.getItem('company_tax_type');
  const showCessRate = localStorage.getItem('showCessRate') || 'N';
  const userId = localStorage.getItem('userId');
  const isAtrPrefix = accessToken === 'atr' || config.PREFIX === 'atr';
  const [qbPromptOpen, setQbPromptOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState('0');

  const toggleOptions = (option) => {
    setSelectedOption(option);
    setFormData((prev) => ({
      ...prev,
      split: option,
    }));
  };

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    cost: '',
    price: '',
    piece_cost: '',
    recommendedSellingBoxPrice: '',
    recommendedSellingPiecePrice: '',
    inner_ean_number: '',
    outer_ean_number: '',
    alert_quantity: '',
    details: '',
    product_details: '',
    slug: '',
    hsn_code: '',
    size: '',
    pack: '',
    bay: '',
    rack: '',
    second_name: '',
    split: '',
    split_price: '',
    price_1: '',
    price_2: '',
    price_3: '',
    price_4: '',
    price_5: '',
    piece_price1: '',
    piece_price2: '',
    piece_price3: '',
    piece_price4: '',
    piece_price5: '',
    band1_special_box_price: '',
    band1_special_piece_price: '',
    band2_special_box_price: '',
    band2_special_piece_price: '',
    band3_special_box_price: '',
    band3_special_piece_price: '',
    band4_special_box_price: '',
    band4_special_piece_price: '',
    band5_special_box_price: '',
    band5_special_piece_price: '',
    band6_special_box_price: '',
    band6_special_piece_price: '',
    is_discount_allow: '',
    percentage: '',
    new_arrival_date: '',
    min_alert_quantity: '',
    max_alert_quantity: '',
    maximum_order_quantity: '',
    featured: '',
    new_arrival: '',
    category_id: '',
    unit: '',
    brand: '',
    barcode_symbology: '',
    tax_rate: '',
    length: '',
    width: '',
    height: '',
    length_unit: '',
    width_unit: '',
    height_unit: '',
    cbm: '',
    net_weight: '',
    gross_weight: '',
    vat_id: '',
    cess_id: '',
    availableFor: '0',
    margin: '',
    piece_margin: '',
  });

  const [errors, setErrors] = useState({});
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [imageName, setImageName] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [unitOptions, setUnitOptions] = useState([]);
  const [vatOptions, setVatOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [cessOptions, setCessOptions] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedVat, setSelectedVat] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCess, setSelectedCess] = useState(null);
  const [dynamicFields, setDynamicFields] = useState([]);
  const [dynamicFormMeta, setDynamicFormMeta] = useState(null);
  const [dynamicOptionsBySource, setDynamicOptionsBySource] = useState({});
  const productDetailsFields = useMemo(() => {
    const detailsSection = dynamicFormMeta?.sections?.find(
      (section) => section.id === "product_details"
    );
    return detailsSection?.fields?.length
      ? detailsSection.fields
      : dynamicFields;
  }, [dynamicFormMeta, dynamicFields]);
  const allDynamicFields = useMemo(() => {
    if (dynamicFormMeta?.sections?.length) {
      return dynamicFormMeta.sections.flatMap((section) => section.fields || []);
    }
    return dynamicFields;
  }, [dynamicFormMeta, dynamicFields]);
  const dynamicFormKeys = useMemo(
    () =>
      new Set(
        allDynamicFields.map((field) => resolveProductFormFieldName(field.name))
      ),
    [allDynamicFields]
  );
  const hasDynamicFields = productDetailsFields.length > 0 || allDynamicFields.length > 0;
  const fieldMatchesName = (field, name) => {
    if (!field) return false;
    const key = resolveProductFormFieldName(name);
    const settingsKey = resolveProductSettingsFieldName(name);
    const fieldKey = resolveProductFormFieldName(field.name);
    const fieldSettings = resolveProductSettingsFieldName(field.name);
    return (
      field.name === name ||
      field.name === key ||
      field.name === settingsKey ||
      fieldKey === key ||
      fieldSettings === settingsKey ||
      fieldSettings === name
    );
  };
  const findDynField = (name) => {
    for (const field of allDynamicFields) {
      if (fieldMatchesName(field, name)) return field;
    }
    const matchNested = (field) => {
      for (const nested of field.fields || []) {
        if (fieldMatchesName(nested, name)) return nested;
        const found = matchNested(nested);
        if (found) return found;
      }
      return null;
    };
    for (const field of allDynamicFields) {
      const found = matchNested(field);
      if (found) return found;
    }
    return null;
  };
  const hasDynField = (name) => {
    const field = findDynField(name);
    if (useDynamicForm && field) return field.visible !== false;
    const key = resolveProductFormFieldName(name);
    return dynamicFormKeys.has(key) || !!field;
  };
  const getDynSection = (sectionId) =>
    dynamicFormMeta?.sections?.find((section) => section.id === sectionId) ||
    null;
  const showDynSection = (sectionId) => {
    if (!hasDynamicFields) return true;
    const section = getDynSection(sectionId);
    if (!section) return false;
    if (!(section.fields || []).length) return false;
    return isSectionVisible(section, { ...formData, split: selectedOption });
  };
  const showBoxPricing =
    (selectedOption == "0" || selectedOption == "2") &&
    (!hasDynamicFields ||
      hasDynField("box_cost") ||
      hasDynField("cost") ||
      hasDynField("recommended_selling_price") ||
      hasDynField("recommendedSellingBoxPrice") ||
      hasDynField("price_box") ||
      hasDynField("special_price_box") ||
      hasDynField("box_prices") ||
      hasDynField("box_margin") ||
      hasDynField("margin") ||
      showDynSection("box_pricing"));
  const showPiecePricing =
    (selectedOption == "1" || selectedOption == "2") &&
    (!hasDynamicFields ||
      hasDynField("piece_cost") ||
      hasDynField("piece_recommended_selling_price") ||
      hasDynField("recommendedSellingPiecePrice") ||
      hasDynField("price_piece") ||
      hasDynField("special_price_piece") ||
      hasDynField("piece_prices") ||
      hasDynField("piece_margin") ||
      showDynSection("piece_pricing"));
  const boxPriceSubFields = useMemo(() => {
    if (!useDynamicForm) return null;
    const resolveTierMap = (fieldName, aliases = []) => {
      const field =
        findDynField(fieldName) ||
        aliases.map((alias) => findDynField(alias)).find(Boolean);
      return resolvePriceTierMapFromField(field, fieldName);
    };
    return mergePriceTierVisibleMap(
      resolveTierMap("price_box", ["box_prices", "box_pricing"]),
      resolveTierMap("special_price_box"),
    );
  }, [allDynamicFields, dynamicFormMeta, useDynamicForm]);
  const piecePriceSubFields = useMemo(() => {
    if (!useDynamicForm) return null;
    const resolveTierMap = (fieldName, aliases = []) => {
      const field =
        findDynField(fieldName) ||
        aliases.map((alias) => findDynField(alias)).find(Boolean);
      return resolvePriceTierMapFromField(field, fieldName);
    };
    return mergePriceTierVisibleMap(
      resolveTierMap("price_piece", ["piece_prices", "piece_pricing"]),
      resolveTierMap("special_price_piece"),
    );
  }, [allDynamicFields, dynamicFormMeta, useDynamicForm]);
  const isPricingFieldVisible = (name, aliases = []) => {
    if (!useDynamicForm || !hasDynamicFields) return true;
    const field =
      findDynField(name) ||
      aliases.map((alias) => findDynField(alias)).find(Boolean);
    if (!field) return false;
    return field.visible !== false;
  };
  const pricingFieldVisibility = useDynamicForm
    ? {
        box_cost: isPricingFieldVisible("box_cost", ["cost"]),
        recommended_selling_price: isPricingFieldVisible(
          "recommended_selling_price",
          ["recommendedSellingBoxPrice"],
        ),
        price_box: isPricingFieldVisible("price_box"),
        special_price_box: isPricingFieldVisible("special_price_box"),
        price_piece: isPricingFieldVisible("price_piece"),
        special_price_piece: isPricingFieldVisible("special_price_piece"),
        piece_cost: isPricingFieldVisible("piece_cost"),
        piece_recommended_selling_price: isPricingFieldVisible(
          "piece_recommended_selling_price",
          ["recommendedSellingPiecePrice"],
        ),
        piece_margin: isPricingFieldVisible("piece_margin"),
        box_margin: isPricingFieldVisible("box_margin", ["margin"]),
      }
    : null;
  const activeBoxSubFields = useDynamicForm
    ? { ...boxPriceSubFields, ...pricingFieldVisibility }
    : null;
  const activePieceSubFields = useDynamicForm
    ? { ...piecePriceSubFields, ...pricingFieldVisibility }
    : null;
  const showSelectSupplier = (() => {
    if (!useDynamicForm) return true;
    if (!hasDynamicFields) return true;
    const field = findDynField("suppliers");
    if (field) return field.visible !== false;
    return showDynSection("suppliers");
  })();
  const optionsBySource = useMemo(
    () => ({
      units: dynamicOptionsBySource.units?.length
        ? dynamicOptionsBySource.units
        : unitOptions,
      vat: dynamicOptionsBySource.vat?.length
        ? dynamicOptionsBySource.vat
        : vatOptions,
      brands: dynamicOptionsBySource.brands?.length
        ? dynamicOptionsBySource.brands
        : brandOptions,
      categories: dynamicOptionsBySource.categories?.length
        ? dynamicOptionsBySource.categories
        : categoryOptions,
      cess: dynamicOptionsBySource.cess?.length
        ? dynamicOptionsBySource.cess
        : cessOptions,
      availability: dynamicOptionsBySource.availability?.length
        ? dynamicOptionsBySource.availability
        : [
            { label: "All", value: "0" },
            { label: "TeleCaller", value: "1" },
          ],
      ...dynamicOptionsBySource,
    }),
    [
      unitOptions,
      vatOptions,
      brandOptions,
      categoryOptions,
      cessOptions,
      dynamicOptionsBySource,
    ]
  );
  const dynamicFormData = useMemo(
    () => ({
      ...formData,
      category: formData.category_id ?? formData.category ?? "",
      tax_rate: formData.tax_rate ?? formData.vat_id ?? "",
      split: formData.split ?? selectedOption,
    }),
    [formData, selectedOption]
  );

  const resolveOptionByValueOrLabel = (options, rawValue) => {
    if (rawValue === undefined || rawValue === null) return null;
    const normalized = String(rawValue).trim().toLowerCase();
    if (!normalized) return null;
    return (
      options.find((opt) => String(opt.value).trim().toLowerCase() === normalized) ||
      options.find((opt) => String(opt.label || "").trim().toLowerCase() === normalized) ||
      null
    );
  };


  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageName(file.name);
      setImagePreview(URL.createObjectURL(file));
      if (errors.product_image) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          product_image: "",
        }));
      }
    }
  };

  const handleRemoveImage = () => {
    setImageName('');
    setImagePreview('');
    document.getElementById("product_image_input").value = "";
  };

  const [supplierList, setSupplierList] = useState([]);
  const [loadedProductCode, setLoadedProductCode] = useState('');

  const fetchProductEditInfo = async () => {
    const apiUrl = `${config.API_URL}/edit_products`;
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const formData = new FormData();
      formData.append("id", productId);
      if (useEncrypt) {
        const ep = encryptPayload({ id: productId });
        if (ep) {
          formData.append("encrypted_payload", ep);
        }
      }
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${jwtToken || accessToken}`,
        },
        body: formData,
      });
      const data = await response.json();
      const resolved = normalizeEncryptedApiResponse(data, useEncrypt);
      const p = resolved.data?.product_data || {};
      const productDetails = p.product_details;
      const plainTextDetails = productDetails !== null ? productDetails.replace(/<\/?[^>]+(>|$)/g, "") : productDetails;
      const rawCategoryId = p.category_id ?? p.category ?? p.categorie_id ?? '';
      const categoryId = rawCategoryId !== null && rawCategoryId !== '' ? String(rawCategoryId) : '';
      setFormData({
        code: p.code,
        name: p.name,
        cost: p.cost,
        price: p.price,
        piece_cost: p.piece_cost,
        inner_ean_number: p.inner_ean_number,
        outer_ean_number: p.outer_ean_number,
        alert_quantity: p.alert_quantity,
        details: p.details,
        product_details: plainTextDetails,
        slug: p.slug,
        hsn_code: p.hsn_code,
        size: p.size,
        pack: p.pack,
        bay: p.bay,
        rack: p.rack,
        second_name: p.second_name,
        split: p.split,
        split_price: p.split_price,
        price_1: p.price_1,
        price_2: p.price_2,
        price_3: p.price_3,
        price_4: p.price_4,
        price_5: p.price_5,
        piece_price1: p.piece_price1,
        piece_price2: p.piece_price2,
        piece_price3: p.piece_price3,
        piece_price4: p.piece_price4,
        piece_price5: p.piece_price5,
        band1_special_box_price: p.offer_box_price || p.band1_special_box_price || '',
        band1_special_piece_price: p.offer_piece_price || p.band1_special_piece_price || '',
        band2_special_box_price: p.offer_box_price1 || p.band2_special_box_price || '',
        band2_special_piece_price: p.offer_piece_price1 || p.band2_special_piece_price || '',
        band3_special_box_price: p.offer_box_price2 || p.band3_special_box_price || '',
        band3_special_piece_price: p.offer_piece_price2 || p.band3_special_piece_price || '',
        band4_special_box_price: p.offer_box_price3 || p.band4_special_box_price || '',
        band4_special_piece_price: p.offer_piece_price3 || p.band4_special_piece_price || '',
        band5_special_box_price: p.offer_box_price4 || p.band5_special_box_price || '',
        band5_special_piece_price: p.offer_piece_price4 || p.band5_special_piece_price || '',
        band6_special_box_price: p.offer_box_price5 || p.band6_special_box_price || '',
        band6_special_piece_price: p.offer_piece_price5 || p.band6_special_piece_price || '',
        is_discount_allow: p.is_discount_allow != null && p.is_discount_allow != '' ? p.is_discount_allow : 0,
        percentage: p.percentage,
        new_arrival_date: p.new_arrival_date,
        min_alert_quantity: p.min_alert_quantity,
        max_alert_quantity: p.max_alert_quantity,
        maximum_order_quantity: p.maximum_order_quantity ?? '',
        featured: p.featured != null && p.featured != '' ? p.featured : 0,
        new_arrival: p.new_arrival != null && p.new_arrival != '' ? p.new_arrival : 0,
        category_id: categoryId,
        unit: p.unit,
        brand: p.brand,
        barcode_symbology: p.barcode_symbology,
        tax_rate: p.tax_rate ? p.tax_rate : '',
        old_box_cost: p.cost,
        old_piece_cost: p.piece_cost,
        old_price: p.price,
        old_image: p.oldimage,
        length: p.length,
        width: p.width,
        height: p.height,
        length_unit: p.length_unit,
        width_unit: p.width_unit,
        height_unit: p.height_unit,
        cbm: p.cbm,
        pallete_ean: p.pallete_ean,
        gross_weight: p.gross_weight,
        net_weight: p.net_weight,
        vat_id: p.tax_rate,
        cess_id: p.cess_rate,
        availableFor: String(p.availableFor) === '1' ? '1' : '0',
        recommendedSellingBoxPrice: p.recommendedSellingBoxPrice,
        recommendedSellingPiecePrice: p.recommendedSellingPiecePrice,
        margin:
          p.price > 0 && p.price > p.cost
            ? (((p.price - p.cost) / p.price) * 100).toFixed(0)
            : "",
        piece_margin:
          p.split_price > 0 && p.split_price > p.piece_cost
            ? (((p.split_price - p.piece_cost) / p.split_price) * 100).toFixed(0)
            : "",
        part_number: p.part_number || "",
      });

      setSelectedOption(p.split);
      setSupplierList(resolved.data?.supplier_data || []);
      setLoadedProductCode(String(p.code ?? ''));
      setImagePreview(p.image);
    } catch (error) {
    }
  };

  const fetchUnitOptions = async () => {
    try {
      const response = await fetch(`${config.API_URL}/get_unit`, {
        method: 'POST',
        headers: { authorization: `Bearer ${accessToken}` }
      });
      const data = await response.json();
      const formattedOptions = (data.data || []).map(item => ({
        label: item.code,
        value: item.id,
      }));
      setUnitOptions(formattedOptions);
    } catch (error) {
    }
  };

  const fetchVatOptions = async () => {
    try {
      const response = await fetch(`${config.API_URL}/get_vat`, {
        method: 'POST',
        headers: { authorization: `Bearer ${accessToken}` }
      });
      const data = await response.json();
      const formattedOptions = (data.data || []).map(item => ({
        label: item.name,
        value: item.id,
      }));
      setVatOptions(formattedOptions);
    } catch (error) {
    }
  };

  const fetchBrandOptions = async () => {
    try {
      const response = await fetch(`${config.API_URL}/get_brands`, {
        method: 'POST',
        headers: { authorization: `Bearer ${accessToken}` }
      });
      const data = await response.json();
      const formattedOptions = (data.data || []).map(item => ({
        label: item.name,
        value: item.id,
      }));
      setBrandOptions(formattedOptions);
    } catch (error) {
    }
  };

  const fetchCategoryOptions = async () => {
    try {
      const response = await fetch(`${config.API_URL}/get_categories`, {
        method: 'POST',
        headers: { authorization: `Bearer ${accessToken}` }
      });
      const data = await response.json();
      const formattedOptions = (data.data || []).map(item => ({
        label: item.name || item.category_name || item.categorie_name || '',
        value: item.id != null && item.id !== '' ? String(item.id) : '',
      })).filter(opt => opt.value !== '');
      setCategoryOptions(formattedOptions);
    } catch (error) {
    }
  };

  const fetchCessOptions = async () => {
    try {
      const response = await fetch(`${config.API_URL}/get_cess`, {
        method: 'POST',
        headers: { authorization: `Bearer ${accessToken}` }
      });
      const data = await response.json();
      const formattedOptions = (data.data || []).map(item => ({
        label: item.name,
        value: item.id,
      }));
      setCessOptions(formattedOptions);
    } catch (error) {
    }
  };

  const fetchPriceVariation = async () => {
    try {
      const data = await postGetPriceVariation(`${config.API_URL}/get_price_variation`, jwtToken || accessToken, {
        draw: 1,
        start: 0,
        length: 1,
      });

      if (data?.isAutogenProductCode === "Y") {
        setIsReadOnly(true);
      } else {
        setIsReadOnly(false);
      }
    } catch {
    }
  };

  const fetchAndApplyPriceVariation = async (basePrice) => {
    if (!basePrice || isNaN(basePrice) || basePrice <= 0) return;
    try {
      const json = await postGetPriceVariation(`${config.API_URL}/get_price_variation`, jwtToken || accessToken, {
        draw: 1,
        start: 0,
        length: 10,
      });
      const rows = Array.isArray(json?.data) ? json.data : [];
      const row = rows[0];
      if (!row) return;
      const p2 = parseFloat(row.percentage2);
      const p3 = parseFloat(row.percentage3);
      const p4 = parseFloat(row.percentage4);
      const p5 = parseFloat(row.percentage5);
      const p6 = parseFloat(row.percentage6);
      const calc = (p) => (isNaN(p) ? "" : ((basePrice * p) / 100).toFixed(2));
      setFormData((prev) => ({
        ...prev,
        price_1: calc(p2),
        price_2: calc(p3),
        price_3: calc(p4),
        price_4: calc(p5),
        price_5: calc(p6),
      }));
    } catch {
    }
  };

  const fetchAndApplyPiecePriceVariation = async (basePrice) => {
    if (!basePrice || isNaN(basePrice) || basePrice <= 0) return;
    try {
      const json = await postGetPriceVariation(`${config.API_URL}/get_price_variation`, jwtToken || accessToken, {
        draw: 1,
        start: 0,
        length: 10,
      });
      const rows = Array.isArray(json?.data) ? json.data : [];
      const row = rows[0];
      if (!row) return;
      const p2 = parseFloat(row.percentage2);
      const p3 = parseFloat(row.percentage3);
      const p4 = parseFloat(row.percentage4);
      const p5 = parseFloat(row.percentage5);
      const p6 = parseFloat(row.percentage6);
      const calc = (p) => (isNaN(p) ? "" : ((basePrice * p) / 100).toFixed(2));
      setFormData((prev) => ({
        ...prev,
        piece_price1: calc(p2),
        piece_price2: calc(p3),
        piece_price3: calc(p4),
        piece_price4: calc(p5),
        piece_price5: calc(p6),
      }));
    } catch {
    }
  };

  const fetchAndApplySpecialBoxPriceVariation = async (basePrice) => {
    if (!basePrice || isNaN(basePrice) || basePrice <= 0) return;
    try {
      const json = await postGetPriceVariation(`${config.API_URL}/get_price_variation`, jwtToken || accessToken, {
        draw: 1,
        start: 0,
        length: 10,
      });
      const rows = Array.isArray(json?.data) ? json.data : [];
      const row = rows[0];
      if (!row) return;
      const p2 = parseFloat(row.percentage2);
      const p3 = parseFloat(row.percentage3);
      const p4 = parseFloat(row.percentage4);
      const p5 = parseFloat(row.percentage5);
      const p6 = parseFloat(row.percentage6);
      const calc = (p) => (isNaN(p) ? "" : ((basePrice * p) / 100).toFixed(2));
      setFormData((prev) => ({
        ...prev,
        band2_special_box_price: calc(p2),
        band3_special_box_price: calc(p3),
        band4_special_box_price: calc(p4),
        band5_special_box_price: calc(p5),
        band6_special_box_price: calc(p6),
      }));
    } catch {
    }
  };

  const fetchAndApplySpecialPiecePriceVariation = async (basePrice) => {
    if (!basePrice || isNaN(basePrice) || basePrice <= 0) return;
    try {
      const json = await postGetPriceVariation(`${config.API_URL}/get_price_variation`, jwtToken || accessToken, {
        draw: 1,
        start: 0,
        length: 10,
      });
      const rows = Array.isArray(json?.data) ? json.data : [];
      const row = rows[0];
      if (!row) return;
      const p2 = parseFloat(row.percentage2);
      const p3 = parseFloat(row.percentage3);
      const p4 = parseFloat(row.percentage4);
      const p5 = parseFloat(row.percentage5);
      const p6 = parseFloat(row.percentage6);
      const calc = (p) => (isNaN(p) ? "" : ((basePrice * p) / 100).toFixed(2));
      setFormData((prev) => ({
        ...prev,
        band2_special_piece_price: calc(p2),
        band3_special_piece_price: calc(p3),
        band4_special_piece_price: calc(p4),
        band5_special_piece_price: calc(p5),
        band6_special_piece_price: calc(p6),
      }));
    } catch {
    }
  };

  useEffect(() => {
    if (!open) {
      setErrors({});
      setSelectedVat(null);
      setSelectedBrand(null);
      setSelectedCess(null);
      setSelectedUnit(null);
      setSelectedCategory(null);
      return;
    }
    setErrors({});
    if (!productId) return;
    const loadData = async () => {
      setSelectedVat(null);
      setSelectedBrand(null);
      setSelectedCess(null);
      setSelectedUnit(null);
      setSelectedCategory(null);
      await Promise.all([
        fetchUnitOptions(),
        fetchVatOptions(),
        fetchBrandOptions(),
        fetchCategoryOptions(),
        showCessRate === "Y" ? fetchCessOptions() : Promise.resolve(),
      ]);
      await fetchProductEditInfo();
      await fetchPriceVariation();
      const auth = jwtToken || accessToken;
      const applyDynamicForm = (form) => {
        const detailsSection = form?.sections?.find(
          (section) => section.id === "product_details"
        );
        const fields = detailsSection?.fields?.length
          ? detailsSection.fields
          : form?.fields || [];
        setDynamicFormMeta(form || null);
        setDynamicFields(fields);
        const allFields = form?.sections?.length
          ? form.sections.flatMap((section) => section.fields || [])
          : form?.fields || [];
        if (!allFields.length) {
          return;
        }
        const storageKeyFor = (resolvedKey) =>
          resolvedKey === "category" ? "category_id" : resolvedKey;
        setFormData((prev) => {
          const next = { ...prev };
          allFields.forEach((field) => {
            const key = resolveProductFormFieldName(field.name);
            const storageKey = storageKeyFor(key);
            const current = next[storageKey];
            const hasDefault =
              field.defaultValue !== undefined &&
              field.defaultValue !== null &&
              field.defaultValue !== "";
            if (
              hasDefault &&
              (current === undefined || current === null || String(current).trim() === "")
            ) {
              next[storageKey] = field.defaultValue;
            } else if (current === undefined) {
              next[storageKey] = "";
            }
          });
          return next;
        });
      };
      if (!useDynamicForm) {
        setDynamicFields([]);
        setDynamicFormMeta(null);
        setDynamicOptionsBySource({});
        return;
      }
      try {
        let form = await fetchDynamicForm({
          formKey: "edit_product",
          authToken: auth,
        });
        const empty =
          !(form?.fields || []).length && !(form?.sections || []).length;
        if (empty) {
          form = await fetchDynamicForm({
            formKey: "add_product",
            authToken: auth,
          });
        }
        applyDynamicForm(form);
        const fields = form?.fields?.length
          ? form.fields
          : (form?.sections || []).flatMap((section) => section.fields || []);
        const optionsMap = await loadProductFormOptionsMap({
          fields,
          authToken: auth,
        });
        setDynamicOptionsBySource(optionsMap);
      } catch {
        setDynamicFields([]);
        setDynamicFormMeta(null);
        setDynamicOptionsBySource({});
      }
    };
    loadData();
  }, [open, productId]);

  useEffect(() => {
    if (unitOptions.length > 0) {
      if (formData.unit) {
        const unitOption = resolveOptionByValueOrLabel(unitOptions, formData.unit);
        if (unitOption) {
          setSelectedUnit(unitOption);
          if (String(formData.unit) !== String(unitOption.value)) {
            setFormData((prev) => ({ ...prev, unit: unitOption.value }));
          }
        } else {
          setSelectedUnit(null);
        }
      } else {
        setSelectedUnit(null);
      }
    }
    if (vatOptions.length > 0) {
      if (formData.vat_id) {
        const vatOption = resolveOptionByValueOrLabel(vatOptions, formData.vat_id);
        setSelectedVat(vatOption || null);
      } else {
        setSelectedVat(null);
      }
    }
    if (brandOptions.length > 0) {
      if (formData.brand) {
        const brandOption = resolveOptionByValueOrLabel(brandOptions, formData.brand);
        setSelectedBrand(brandOption || null);
      } else {
        setSelectedBrand(null);
      }
    }
    if (categoryOptions.length > 0) {
      if (formData.category_id) {
        const categoryOption = resolveOptionByValueOrLabel(categoryOptions, formData.category_id);
        if (categoryOption) {
          setSelectedCategory(categoryOption);
          if (String(formData.category_id) !== String(categoryOption.value)) {
            setFormData((prev) => ({ ...prev, category_id: categoryOption.value }));
          }
        } else {
          setSelectedCategory(null);
        }
      } else {
        setSelectedCategory(null);
      }
    }
    if (showCessRate === "Y" && cessOptions.length > 0) {
      if (formData.cess_id) {
        const cessOption = resolveOptionByValueOrLabel(cessOptions, formData.cess_id);
        setSelectedCess(cessOption || null);
      } else {
        setSelectedCess(null);
      }
    }
  }, [unitOptions, vatOptions, brandOptions, categoryOptions, cessOptions, formData.unit, formData.vat_id, formData.brand, formData.category_id, formData.cess_id]);

  useEffect(() => {
    const next = computeCbmFromDimensions(
      formData.length,
      formData.width,
      formData.height,
      formData.length_unit,
      formData.width_unit,
      formData.height_unit
    );
    setFormData((prev) => (prev.cbm === next ? prev : { ...prev, cbm: next }));
  }, [formData.length, formData.width, formData.height, formData.length_unit, formData.width_unit, formData.height_unit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    const costValue =
      name === "cost" ? parseFloat(value) : parseFloat(formData.cost);
    const priceValue =
      name === "price" ? parseFloat(value) : parseFloat(formData.price);

    const pieceCostValue =
      name === "piece_cost"
        ? parseFloat(value)
        : parseFloat(formData.piece_cost);
    const splitPriceValue =
      name === "split_price"
        ? parseFloat(value)
        : parseFloat(formData.split_price);

    let fieldErrors = {};

    if (name === "price" || name === "cost") {
      if (priceValue > 0 && priceValue > costValue) {
        const margin = ((priceValue - costValue) / priceValue) * 100;
        setFormData((prev) => ({
          ...prev,
          price: name === "price" ? priceValue : prev.price,
          cost: name === "cost" ? costValue : prev.cost,
          margin: margin.toFixed(0),
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          price: name === "price" ? priceValue : prev.price,
          cost: name === "cost" ? costValue : prev.cost,
          margin: "",
        }));
      }
    }

    if (name === "split_price" || name === "piece_cost") {
      if (splitPriceValue > 0 && splitPriceValue > pieceCostValue) {
        const pieceMargin =
          ((splitPriceValue - pieceCostValue) / splitPriceValue) * 100;
        setFormData((prev) => ({
          ...prev,
          split_price:
            name === "split_price" ? splitPriceValue : prev.split_price,
          piece_cost:
            name === "piece_cost" ? pieceCostValue : prev.piece_cost,
          piece_margin: pieceMargin.toFixed(0),
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          split_price:
            name === "split_price" ? splitPriceValue : prev.split_price,
          piece_cost:
            name === "piece_cost" ? pieceCostValue : prev.piece_cost,
          piece_margin: "",
        }));
      }
    }

    if (name === "recommendedSellingBoxPrice") {
      const recommendedSellingBoxPrice = parseFloat(value);
      if (recommendedSellingBoxPrice < 0) {
        fieldErrors.recommendedSellingBoxPrice = "Price must be a positive number.";
      } else {
        fieldErrors.recommendedSellingBoxPrice = '';
      }
    }


    if (name === "recommendedSellingPiecePrice") {
      const recommendedSellingPiecePrice = parseFloat(value);
      if (recommendedSellingPiecePrice < 0) {
        fieldErrors.recommendedSellingPiecePrice = "Price must be a positive number.";
      } else {
        fieldErrors.recommendedSellingPiecePrice = '';
      }
    }


    if (name === "code") {
      const slugVal = value;
      setFormData((prevState) => ({
        ...prevState,
        slug: slugVal,
      }));
    }

    if (name === 'pack') {

      const numericValue = parseFloat(value);

      if (isNaN(numericValue) || numericValue < 0) {
        fieldErrors[name] = "Pack must be a positive number.";
        setFormData((prev) => ({ ...prev, [name]: 0 }));
        fieldErrors[name] = "Pack must be a positive number.";
        setFormData((prev) => ({ ...prev, [name]: 0 }));
      } else {
        fieldErrors[name] = '';
        setFormData((prev) => ({ ...prev, [name]: numericValue }));
      }
    }


    const isSelectCostBoxHidden = selectedOption == '0' || selectedOption == '2';
    const isSelectPieceBoxHidden = selectedOption == '1' || selectedOption == '2';


    switch (name) {
      case "code":
        if (!value) {
          fieldErrors.code = "Mention code.";
        } else if (value.length < 2) {
          fieldErrors.code = "code must consist of at least 2 characters.";
        } else if (value.length > 255) {
          fieldErrors.code = "code must not exceed 255 characters.";
        }
        if (!fieldErrors.code) {
          fieldErrors.code = '';
        }
        break;

      case "name":
        if (!value) {
          fieldErrors.name = "Mention name.";
        } else if (value.length < 2) {
          fieldErrors.name = "name must consist of at least 2 characters.";
        } else if (value.length > 255) {
          fieldErrors.name = "name must not exceed 255 characters.";
        }
        if (!fieldErrors.name) {
          fieldErrors.name = '';
        }
        break;



      case 'size':
        if (!value) {
          fieldErrors.size = "Mention size.";
        } else if (value.length < 2) {
          fieldErrors.size = "size must consist of at least 2 characters.";
        } else if (value.length > 255) {
          fieldErrors.size = "size must not exceed 255 characters.";
        }
        if (!fieldErrors.size) {
          fieldErrors.size = '';
        }
        break;

      case "pack":
        if (!value) {
          fieldErrors.pack = "Mention pack.";
        } else if (value.length < 1) {
          fieldErrors.pack = "pack must consist of at least 1 digit.";
        } else if (value.length > 255) {
          fieldErrors.pack = "pack must not exceed 255 digit.";
        }
        if (!fieldErrors.pack) {
          fieldErrors.pack = '';
        }
        break;




      case 'cost':
        if (!isSelectCostBoxHidden) break;
        if (!value) {
          fieldErrors.cost = "Mention cost.";
        } else if (value.length < 1) {
          fieldErrors.cost = "cost must consist of at least 1 digit.";
        } else if (value.length > 255) {
          fieldErrors.cost = "cost must not exceed 255 digit.";
        }
        if (!fieldErrors.cost) {
          fieldErrors.cost = '';
        }
        break;

      case 'price':
        if (!isSelectCostBoxHidden) break;
        if (!value) {
          fieldErrors.price = "Mention price.";
        } else if (value < 0) {
          fieldErrors.price = "Mention positive.";
          fieldErrors.price = "Mention positive.";
        } else if (value.price > costValue) {
          fieldErrors.price = "price not less than  cost.";
        } else if (value.price < 1) {
        } else if (value.price < 1) {
          fieldErrors.price = "price must consist of at least 1 digit.";
        } else if (value.price > 255) {
          fieldErrors.price = "price must not exceed 255 digit.";
        }
        if (!fieldErrors.price) {
          fieldErrors.price = '';
        }
        break;

      case 'recommendedSellinBoxPrice':
        if (!isSelectCostBoxHidden) break;
        if (!value) {
          fieldErrors.recommendedSellinBoxPrice = "Mention price.";
        } else if (value < 0) {
          fieldErrors.recommendedSellinBoxPrice = "Mention positive.";
        } else if (value.price > costValue) {
          fieldErrors.recommendedSellinBoxPrice = "price not less than  cost.";
        } else if (value.price < 1) {
          fieldErrors.recommendedSellinBoxPrice = "price must consist of at least 1 digit.";
        } else if (value.price > 255) {
          fieldErrors.recommendedSellinBoxPrice = "price must not exceed 255 digit.";
        }
        if (!fieldErrors.recommendedSellinBoxPrice) {
          fieldErrors.recommendedSellinBoxPrice = '';
        }
        break;

      case 'price_1':
        if (!isSelectCostBoxHidden) break;
        if (!value) {
          fieldErrors.price_1 = "Mention price.";
        } else if (value < 0) {
          fieldErrors.price_1 = "Mention positive.";
        } else if (value < 0) {
          fieldErrors.price_1 = "Mention positive.";
        } else if (value.price_1 < 1) {
          fieldErrors.price_1 = "price must consist of at least 1 digit.";
        } else if (value.price_1 > 255) {
          fieldErrors.price_1 = "price must not exceed 255 digit.";
        }
        if (!fieldErrors.price_1) {
          fieldErrors.price_1 = '';
        }
        break;

      case 'price_2':
        if (!isSelectCostBoxHidden) break;
        if (!value) {
          fieldErrors.price_2 = "Mention price.";
        } else if (value < 0) {
          fieldErrors.price_2 = "Mention positive.";
        } else if (value.price_2 < 1) {
        } else if (value < 0) {
          fieldErrors.price_2 = "Mention positive.";
        } else if (value.price_2 < 1) {
          fieldErrors.price_2 = "price must consist of at least 1 digit.";
        } else if (value.price_2 > 255) {
          fieldErrors.price_2 = "price must not exceed 255 digit.";
        }
        if (!fieldErrors.price_2) {
          fieldErrors.price_2 = '';
        }
        break;

      case 'price_3':
        if (!isSelectCostBoxHidden) break;
        if (!value) {
          fieldErrors.price_3 = "Mention price.";
        } else if (value < 0) {
          fieldErrors.price_3 = "Mention positive.";
          fieldErrors.price_3 = "Mention positive.";
        } else if (value.price_3 < 1) {
          fieldErrors.price_3 = "price must consist of at least 1 digit.";
        } else if (value.price_3 > 255) {
          fieldErrors.price_3 = "price must not exceed 255 digit.";
        }
        if (!fieldErrors.price_3) {
          fieldErrors.price_3 = '';
        }
        break;

      case 'price_4':
        if (!isSelectCostBoxHidden) break;
        if (!value) {
          fieldErrors.price_4 = "Mention price.";
        } else if (value < 0) {
          fieldErrors.price_4 = "Mention positive.";
          fieldErrors.price_4 = "Mention positive.";
        } else if (value.price_4 < 1) {
          fieldErrors.price_4 = "price must consist of at least 1 digit.";
        } else if (value.price_4 > 255) {
          fieldErrors.price_4 = "price must not exceed 255 digit.";
        }
        if (!fieldErrors.price_4) {
          fieldErrors.price_4 = '';
        }
        break;

      case 'price_5':
        if (!isSelectCostBoxHidden) break;
        if (!value) {
          fieldErrors.price_5 = "Mention price.";
        } else if (value < 0) {
          fieldErrors.price_5 = "Mention positive.";
          fieldErrors.price_5 = "Mention positive.";
        } else if (value.price_5 < 1) {
          fieldErrors.price_5 = "price must consist of at least 1 digit.";
        } else if (value.price_5 > 255) {
          fieldErrors.price_5 = "price must not exceed 255 digit.";
        }
        if (!fieldErrors.price_5) {
          fieldErrors.price_5 = '';
        }
        break;

      case 'piece_cost':
        if (!isSelectPieceBoxHidden) break;
        if (!value) {
          fieldErrors.piece_cost = "Mention cost.";
        } else if (value < 0) {
          fieldErrors.piece_cost = "Mention positive.";
          fieldErrors.piece_cost = "Mention positive.";
        } else if (value.length < 1) {
          fieldErrors.piece_cost = "cost must consist of at least 1 digit.";
        } else if (value.length > 255) {
          fieldErrors.piece_cost = "cost must not exceed 255 digit.";
        }
        if (!fieldErrors.piece_cost) {
          fieldErrors.piece_cost = '';
        }
        break;
      case 'recommendedSellingPiecePrice':
        if (!isSelectPieceBoxHidden) break;
        if (!value) {
          fieldErrors.recommendedSellingPiecePrice = "Mention cost.";
        } else if (value < 0) {
          fieldErrors.recommendedSellingPiecePrice = "Mention positive.";
        } else if (value.length < 1) {
          fieldErrors.recommendedSellingPiecePrice = "cost must consist of at least 1 digit.";
        } else if (value.length > 255) {
          fieldErrors.recommendedSellingPiecePrice = "cost must not exceed 255 digit.";
        }
        if (!fieldErrors.recommendedSellingPiecePrice) {
          fieldErrors.recommendedSellingPiecePrice = '';
        }
        break;
      case 'split_price':
        if (!isSelectPieceBoxHidden) break;
        if (!value) {
          fieldErrors.split_price = "Mention price.";
        } else if (value < 0) {
          fieldErrors.split_price = "Mention positive.";
          fieldErrors.split_price = "Mention positive.";
        } else if (value.split_price < 1) {
          fieldErrors.split_price = "price must consist of at least 1 digit.";
        } else if (value.split_price > 255) {
          fieldErrors.split_price = "price must not exceed 255 digit.";
        }
        if (!fieldErrors.split_price) {
          fieldErrors.split_price = '';
        }
        break;

      case 'piece_price1':
        if (!isSelectPieceBoxHidden) break;
        if (!value) {
          fieldErrors.piece_price1 = "Mention price.";
        } else if (value < 0) {
          fieldErrors.piece_price1 = "Mention positive.";
          fieldErrors.piece_price1 = "Mention positive.";
        } else if (value.piece_price1 < 1) {
          fieldErrors.piece_price1 = "price must consist of at least 1 digit.";
        } else if (value.piece_price1 > 255) {
          fieldErrors.piece_price1 = "price must not exceed 255 digit.";
        }
        if (!fieldErrors.piece_price1) {
          fieldErrors.piece_price1 = '';
        }
        break;

      case 'piece_price2':
        if (!isSelectPieceBoxHidden) break;
        if (!value) {
          fieldErrors.piece_price2 = "Mention price.";
        } else if (value < 0) {
          fieldErrors.piece_price2 = "Mention positive.";
          fieldErrors.piece_price2 = "Mention positive.";
        } else if (value.piece_price2 < 1) {
          fieldErrors.piece_price2 = "price must consist of at least 1 digit.";
        } else if (value.piece_price2 > 255) {
          fieldErrors.piece_price2 = "price must not exceed 255 digit.";
        }
        if (!fieldErrors.piece_price2) {
          fieldErrors.piece_price2 = '';
        }
        break;

      case 'piece_price3':
        if (!isSelectPieceBoxHidden) break;
        if (!value) {
          fieldErrors.piece_price3 = "Mention price.";
        } else if (value < 0) {
          fieldErrors.piece_price3 = "Mention positive.";
          fieldErrors.piece_price3 = "Mention positive.";
        } else if (value.piece_price3 < 1) {
          fieldErrors.piece_price3 = "price must consist of at least 1 digit.";
        } else if (value.piece_price3 > 255) {
          fieldErrors.piece_price3 = "price must not exceed 255 digit.";
        }
        if (!fieldErrors.piece_price3) {
          fieldErrors.piece_price3 = '';
        }
        break;

      case 'piece_price4':
        if (!isSelectPieceBoxHidden) break;
        if (!value) {
          fieldErrors.piece_price4 = "Mention price.";
        } else if (value < 0) {
          fieldErrors.piece_price4 = "Mention positive.";
          fieldErrors.piece_price4 = "Mention positive.";
        } else if (value.piece_price4 < 1) {
          fieldErrors.piece_price4 = "price must consist of at least 1 digit.";
        } else if (value.piece_price4 > 255) {
          fieldErrors.piece_price4 = "price must not exceed 255 digit.";
        }
        if (!fieldErrors.piece_price4) {
          fieldErrors.piece_price4 = '';
        }
        break;

      case 'piece_price5':
        if (!isSelectPieceBoxHidden) break;
        if (!value) {
          fieldErrors.piece_price5 = "Mention price.";
        } else if (value < 0) {
          fieldErrors.piece_price5 = "Mention positive.";
          fieldErrors.piece_price5 = "Mention positive.";
        } else if (value.piece_price5 < 1) {
          fieldErrors.piece_price5 = "price must consist of at least 1 digit.";
        } else if (value.piece_price5 > 255) {
          fieldErrors.piece_price5 = "price must not exceed 255 digit.";
        }
        if (!fieldErrors.piece_price5) {
          fieldErrors.piece_price5 = '';
        }
        break;

      default:
        break;
    }



    setErrors((prevErrors) => ({
      ...prevErrors,
      ...fieldErrors,
    }));


    // Clear error for this field when user starts typing (for fields not handled above)
    if (errors[name] && !fieldErrors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const priceValue = parseFloat(formData.price) || 0;
    const costValue = parseFloat(formData.cost) || 0;
    const splitPriceValue = parseFloat(formData.split_price) || 0;
    const pieceCostValue = parseFloat(formData.piece_cost) || 0;

    setTimeout(() => {
      const fieldError = errors[name];
      if (fieldError) {
        toast.error(fieldError);
      }
    }, 100);

    if (name === "price" || name === "cost") {
      if (priceValue > 0 && costValue > 0 && priceValue <= costValue) {
        toast.error("Price must be greater than cost.");
      }
      if (name === "price") {
        const basePrice = parseFloat(value);
        if (!isNaN(basePrice) && basePrice > 0) fetchAndApplyPriceVariation(basePrice);
      }
    }

    if (name === "split_price" || name === "piece_cost") {
      if (splitPriceValue > 0 && pieceCostValue > 0 && splitPriceValue <= pieceCostValue) {
        toast.error("Piece price must be greater than piece cost.");
      }
      if (name === "split_price") {
        const basePrice = parseFloat(value);
        if (!isNaN(basePrice) && basePrice > 0) fetchAndApplyPiecePriceVariation(basePrice);
      }
    }

    if (name === "band1_special_box_price") {
      const basePrice = parseFloat(value);
      if (!isNaN(basePrice) && basePrice > 0) fetchAndApplySpecialBoxPriceVariation(basePrice);
    }

    if (name === "band1_special_piece_price") {
      const basePrice = parseFloat(value);
      if (!isNaN(basePrice) && basePrice > 0) fetchAndApplySpecialPiecePriceVariation(basePrice);
    }
  };

  const handleDynamicFieldChange = (e) => {
    const { name, value } = e.target;
    if (name === "split") {
      setSelectedOption(String(value));
    }
    if (name === "unit") {
      setSelectedUnit(
        unitOptions.find((option) => String(option.value) === String(value)) ||
          null
      );
    }
    if (name === "tax_rate") {
      setSelectedVat(
        vatOptions.find((option) => String(option.value) === String(value)) ||
          null
      );
      setFormData((prev) => ({
        ...prev,
        tax_rate: value,
        vat_id: value,
      }));
      if (errors.vat_id) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          vat_id: "",
        }));
      }
      return;
    }
    if (name === "brand") {
      setSelectedBrand(
        brandOptions.find((option) => String(option.value) === String(value)) ||
          null
      );
    }
    if (name === "category") {
      setSelectedCategory(
        categoryOptions.find((option) => String(option.value) === String(value)) ||
          null
      );
      handleChange({ target: { name: "category_id", value } });
      return;
    }
    if (name === "code") {
      setFormData((prev) => ({
        ...prev,
        code: value,
        slug: value,
      }));
      handleChange({ target: { name: "code", value } });
      return;
    }
    handleChange(e);
    if (
      name === "length" ||
      name === "width" ||
      name === "height" ||
      name === "length_unit" ||
      name === "width_unit" ||
      name === "height_unit"
    ) {
      const nextLength = name === "length" ? value : formData.length;
      const nextWidth = name === "width" ? value : formData.width;
      const nextHeight = name === "height" ? value : formData.height;
      const nextLengthUnit =
        name === "length_unit"
          ? String(value || "mm").toLowerCase()
          : formData.length_unit || "mm";
      const nextWidthUnit =
        name === "width_unit"
          ? String(value || "mm").toLowerCase()
          : formData.width_unit || "mm";
      const nextHeightUnit =
        name === "height_unit"
          ? String(value || "mm").toLowerCase()
          : formData.height_unit || "mm";
      const cbm = computeCbmFromDimensions(
        nextLength,
        nextWidth,
        nextHeight,
        nextLengthUnit,
        nextWidthUnit,
        nextHeightUnit
      );
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        cbm,
        length_unit: nextLengthUnit,
        width_unit: nextWidthUnit,
        height_unit: nextHeightUnit,
      }));
    }
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: checked ? '1' : '0',
    }));
    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }));
    }
  };

  const handleUnitChange = (event, newValue) => {
    setSelectedUnit(newValue);
    const value = newValue ? newValue.value : "";
    setFormData((prevData) => ({
      ...prevData,
      unit: value,
    }));
    if (errors.unit) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        unit: "",
      }));
    }
  };

  const handleVatChange = (event, newValue) => {
    setSelectedVat(newValue);
    const value = newValue ? newValue.value : "";
    setFormData((prevData) => ({
      ...prevData,
      vat_id: value,
      tax_rate: value,
    }));
    if (errors.vat_id) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        vat_id: "",
      }));
    }
  };

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
    const value = newValue ? newValue.value : "";
    setFormData((prevData) => ({
      ...prevData,
      category_id: value,
    }));
    if (errors.category_id) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        category_id: "",
      }));
    }
  };

  const handleBrandChange = (event, newValue) => {
    setSelectedBrand(newValue);
    const value = newValue ? newValue.value : "";
    setFormData((prevData) => ({
      ...prevData,
      brand: value,
    }));
  };

  const handleCessChange = (event, newValue) => {
    setSelectedCess(newValue);
    const value = newValue ? newValue.value : "";
    setFormData((prevData) => ({
      ...prevData,
      cess_id: value,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    const priceErrors = [];

    if (hasDynamicFields) {
      const seenNames = new Set();
      const mergedFields = [];
      [...allDynamicFields, ...productDetailsFields, ...dynamicFields].forEach((field) => {
        if (!field?.name || seenNames.has(field.name)) return;
        seenNames.add(field.name);
        mergedFields.push(field);
      });
      mergedFields.forEach((field) => {
        if (!field.required || field.visible === false) return;
        const key = resolveProductFormFieldName(field.name);
        const settingsKey = resolveProductSettingsFieldName(field.name);
        const storageKey = key === "category" ? "category_id" : key;
        if (
          field.type === "file" ||
          key === "product_image" ||
          settingsKey === "product_image"
        ) {
          const hasImage =
            !!imagePreview ||
            !!imageName ||
            !!document.getElementById("product_image_input")?.files?.length;
          if (!hasImage) {
            newErrors.product_image = `${field.label || "Product Image"} is required.`;
          }
          return;
        }
        let value = formData[storageKey] ?? formData[key];
        if (key === "tax_rate") {
          value = value ?? formData.vat_id;
        }
        if (
          field.type === "checkbox" ||
          key === "new_arrival" ||
          key === "is_discount_allow" ||
          key === "featured"
        ) {
          if (value !== true && value !== 1 && value !== "1") {
            newErrors[key] = `${field.label || key} is required.`;
          }
          return;
        }
        if (
          field.type === "repeater" ||
          field.type === "relation_table" ||
          field.type === "display" ||
          (field.fields || []).length
        ) {
          return;
        }
        if (
          field.name === "price_box" ||
          field.name === "special_price_box" ||
          field.name === "price_piece" ||
          field.name === "special_price_piece"
        ) {
          return;
        }
        if (field.name === "product_type") {
          if (selectedOption === "" || selectedOption == null) {
            newErrors.split = `${field.label || "Product Type"} is required.`;
          }
          return;
        }
        if (value === undefined || value === null || String(value).trim() === "") {
          newErrors[storageKey] = `${field.label || storageKey} is required.`;
        }
      });

      setErrors(newErrors);
      if (Object.keys(newErrors).length === 0) return true;
      return newErrors;
    }

    if (!formData.code) {
      newErrors.code = "Mention code.";
    } else if (formData.code.length < 2) {
      newErrors.code = "code must consist of at least 2 characters.";
    } else if (formData.code.length > 255) {
      newErrors.code = "code must not exceed 255 characters.";
    }

    if (!formData.name) {
      newErrors.name = "Mention name.";
    } else if (formData.name.length < 2) {
      newErrors.name = "name must consist of at least 2 characters.";
    } else if (formData.name.length > 255) {
      newErrors.name = "name must not exceed 255 characters.";
    }



    if (!formData.pack) {
      newErrors.pack = "Mention pack.";
    } else if (formData.pack.length < 1) {
      newErrors.pack = "pack must consist of at least 1 digit.";
    } else if (formData.pack.length > 255) {
      newErrors.pack = "pack must not exceed 255 characters.";
    }

    const isSelectCostBoxHidden =
      selectedOption == "0" || selectedOption == "2";
    if (isSelectCostBoxHidden) {
      if (!formData.cost) {
        newErrors.cost = "Mention cost.";
        priceErrors.push("Box Cost: Mention cost.");
      } else if (formData.cost.length < 1) {
        newErrors.cost = "cost must consist of at least 1 digit.";
        priceErrors.push("Box Cost: cost must consist of at least 1 digit.");
      } else if (formData.cost.length > 255) {
        newErrors.cost = "cost must not exceed 255 digits.";
        priceErrors.push("Box Cost: cost must not exceed 255 digits.");
      }

      // if (!formData.recommendedSellingBoxPrice) {
      //   newErrors.recommendedSellingBoxPrice = "Mention Price.";
      // } else if (formData.recommendedSellingBoxPrice.length < 1) {
      //   newErrors.recommendedSellingBoxPrice = "Price must consist of at least 1 digit.";
      // } else if (formData.recommendedSellingBoxPrice.length > 255) {
      //   newErrors.recommendedSellingBoxPrice = "Price must not exceed 255 digits.";
      // }

      if (!formData.price) {
        newErrors.price = "Mention price.";
        priceErrors.push("Band 1 - Price (Box): Mention price.");
      } else if (formData.price.length < 1) {
        newErrors.price = "price must consist of at least 1 digit.";
        priceErrors.push(
          "Band 1 - Price (Box): price must consist of at least 1 digit."
        );
      } else if (formData.price.length > 255) {
        newErrors.price = "price must not exceed 255 digits.";
        priceErrors.push(
          "Band 1 - Price (Box): price must not exceed 255 digits."
        );
      }

      if (!formData.price_1) {
        newErrors.price_1 = "Mention price.";
        priceErrors.push("Band 2 - Price (Box): Mention price.");
      } else if (formData.price_1.length < 1) {
        newErrors.price_1 = "price must consist of at least 1 digit.";
        priceErrors.push(
          "Band 2 - Price (Box): price must consist of at least 1 digit."
        );
      } else if (formData.price_1.length > 255) {
        newErrors.price_1 = "price must not exceed 255 digits.";
        priceErrors.push(
          "Band 2 - Price (Box): price must not exceed 255 digits."
        );
      }

      if (!formData.price_2) {
        newErrors.price_2 = "Mention price.";
        priceErrors.push("Band 3 - Price (Box): Mention price.");
      } else if (formData.price_2.length < 1) {
        newErrors.price_2 = "price must consist of at least 1 digit.";
        priceErrors.push(
          "Band 3 - Price (Box): price must consist of at least 1 digit."
        );
      } else if (formData.price_2.length > 255) {
        newErrors.price_2 = "price must not exceed 255 digits.";
        priceErrors.push(
          "Band 3 - Price (Box): price must not exceed 255 digits."
        );
      }

      if (!formData.price_3) {
        newErrors.price_3 = "Mention price.";
        priceErrors.push("Band 4 - Price (Box): Mention price.");
      } else if (formData.price_3.length < 1) {
        newErrors.price_3 = "price must consist of at least 1 digit.";
        priceErrors.push(
          "Band 4 - Price (Box): price must consist of at least 1 digit."
        );
      } else if (formData.price_3.length > 255) {
        newErrors.price_3 = "price must not exceed 255 digits.";
        priceErrors.push(
          "Band 4 - Price (Box): price must not exceed 255 digits."
        );
      }

      if (!formData.price_4) {
        newErrors.price_4 = "Mention price.";
        priceErrors.push("Band 5 - Price (Box): Mention price.");
      } else if (formData.price_4.length < 1) {
        newErrors.price_4 = "price must consist of at least 1 digit.";
        priceErrors.push(
          "Band 5 - Price (Box): price must consist of at least 1 digit."
        );
      } else if (formData.price_4.length > 255) {
        newErrors.price_4 = "price must not exceed 255 digits.";
        priceErrors.push(
          "Band 5 - Price (Box): price must not exceed 255 digits."
        );
      }

      if (!formData.price_5) {
        newErrors.price_5 = "Mention price.";
        priceErrors.push("Band 6 - Price (Box): Mention price.");
      } else if (formData.price_5.length < 1) {
        newErrors.price_5 = "price must consist of at least 1 digit.";
        priceErrors.push(
          "Band 6 - Price (Box): price must consist of at least 1 digit."
        );
      } else if (formData.price_5.length > 255) {
        newErrors.price_5 = "price must not exceed 255 digits.";
        priceErrors.push(
          "Band 6 - Price (Box): price must not exceed 255 digits."
        );
      }
    }

    const isSelectPieceBoxHidden =
      selectedOption == "1" || selectedOption == "2";
    if (isSelectPieceBoxHidden) {
      if (!formData.piece_cost) {
        newErrors.piece_cost = "Mention cost.";
        priceErrors.push("Piece Cost: Mention cost.");
      } else if (formData.piece_cost.length < 1) {
        newErrors.piece_cost = "cost must consist of at least 1 digit.";
        priceErrors.push("Piece Cost: cost must consist of at least 1 digit.");
      } else if (formData.piece_cost.length > 255) {
        newErrors.piece_cost = "cost must not exceed 255 digits.";
        priceErrors.push("Piece Cost: cost must not exceed 255 digits.");
      }

      if (!formData.split_price) {
        newErrors.split_price = "Mention price.";
        priceErrors.push("Band 1 - Price (Piece): Mention price.");
      } else if (formData.split_price.length < 1) {
        newErrors.split_price = "price must consist of at least 1 digit.";
        priceErrors.push(
          "Band 1 - Price (Piece): price must consist of at least 1 digit."
        );
      } else if (formData.split_price.length > 255) {
        newErrors.split_price = "price must not exceed 255 digits.";
        priceErrors.push(
          "Band 1 - Price (Piece): price must not exceed 255 digits."
        );
      }

      // if (!formData.recommendedSellingPiecePrice) {
      //   newErrors.recommendedSellingPiecePrice = "Mention Price.";
      // } else if (formData.recommendedSellingPiecePrice.length < 1) {
      //   newErrors.recommendedSellingPiecePrice = "Price must consist of at least 1 digit.";
      // } else if (formData.recommendedSellingPiecePrice.length > 255) {
      //   newErrors.piecerecommendedSellingPiecePrice = "Price must not exceed 255 digits.";
      // }

      if (!formData.piece_price1) {
        newErrors.piece_price1 = "Mention price.";
        priceErrors.push("Band 2 - Price (Piece): Mention price.");
      } else if (formData.piece_price1.length < 1) {
        newErrors.piece_price1 = "price must consist of at least 1 digit.";
        priceErrors.push(
          "Band 2 - Price (Piece): price must consist of at least 1 digit."
        );
      } else if (formData.piece_price1.length > 255) {
        newErrors.piece_price1 = "price must not exceed 255 digits.";
        priceErrors.push(
          "Band 2 - Price (Piece): price must not exceed 255 digits."
        );
      }

      if (!formData.piece_price2) {
        newErrors.piece_price2 = "Mention price.";
        priceErrors.push("Band 3 - Price (Piece): Mention price.");
      } else if (formData.piece_price2.length < 1) {
        newErrors.piece_price2 = "price must consist of at least 1 digit.";
        priceErrors.push(
          "Band 3 - Price (Piece): price must consist of at least 1 digit."
        );
      } else if (formData.piece_price2.length > 255) {
        newErrors.piece_price2 = "price must not exceed 255 digits.";
        priceErrors.push(
          "Band 3 - Price (Piece): price must not exceed 255 digits."
        );
      }

      if (!formData.piece_price3) {
        newErrors.piece_price3 = "Mention price.";
        priceErrors.push("Band 4 - Price (Piece): Mention price.");
      } else if (formData.piece_price3.length < 1) {
        newErrors.piece_price3 = "price must consist of at least 1 digit.";
        priceErrors.push(
          "Band 4 - Price (Piece): price must consist of at least 1 digit."
        );
      } else if (formData.piece_price3.length > 255) {
        newErrors.piece_price3 = "price must not exceed 255 digits.";
        priceErrors.push(
          "Band 4 - Price (Piece): price must not exceed 255 digits."
        );
      }

      if (!formData.piece_price4) {
        newErrors.piece_price4 = "Mention price.";
        priceErrors.push("Band 5 - Price (Piece): Mention price.");
      } else if (formData.piece_price4.length < 1) {
        newErrors.piece_price4 = "price must consist of at least 1 digit.";
        priceErrors.push(
          "Band 5 - Price (Piece): price must consist of at least 1 digit."
        );
      } else if (formData.piece_price4.length > 255) {
        newErrors.piece_price4 = "price must not exceed 255 digits.";
        priceErrors.push(
          "Band 5 - Price (Piece): price must not exceed 255 digits."
        );
      }

      if (!formData.piece_price5) {
        newErrors.piece_price5 = "Mention price.";
        priceErrors.push("Band 6 - Price (Piece): Mention price.");
      } else if (formData.piece_price5.length < 1) {
        newErrors.piece_price5 = "price must consist of at least 1 digit.";
        priceErrors.push(
          "Band 6 - Price (Piece): price must consist of at least 1 digit."
        );
      } else if (formData.piece_price5.length > 255) {
        newErrors.piece_price5 = "price must not exceed 255 digits.";
        priceErrors.push(
          "Band 6 - Price (Piece): price must not exceed 255 digits."
        );
      }
    }

    // Validation for mandatory fields
    if (!formData.size || formData.size === "0") {
      newErrors.size = "Size is required.";
    }

    if (!formData.unit || formData.unit === "0") {
      const unitFallback = selectedUnit?.value;
      if (!unitFallback || String(unitFallback) === "0") {
        newErrors.unit = "Unit is required.";
      }
    }

    if (!formData.vat_id || formData.vat_id === "0") {
      newErrors.vat_id = "VAT is required.";
    }

    if (!formData.category_id || formData.category_id === "0") {
      const categoryFallback = selectedCategory?.value;
      if (!categoryFallback || String(categoryFallback) === "0") {
        newErrors.category_id = "Category is required.";
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0 || priceErrors.length > 0) {
      const message = [...Object.values(newErrors), ...priceErrors].join('\n');
      return { newErrors, priceErrors, message };
    }
    return true;
  };

  const duplicateCheckColumnIds = [
    'code',
    'name',
    'categories',
    'brand',
    'size',
    'box_cost',
    'price',
    'box_diff',
    'quantity',
    'piece_cost',
    'piece_price',
    'piece_diff',
    'split_quantity',
  ];

  const checkDuplicateProductCodeBeforeUpdate = async () => {
    const code = String(formData.code || '').trim();
    if (!code) {
      return { ok: true };
    }
    if (String(loadedProductCode || '').trim() === code) {
      return { ok: true };
    }
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const headers = {
        authorization: `Bearer ${jwtToken || accessToken}`,
      };
      let body;
      if (useEncrypt) {
        const requestData = {
          draw: 1,
          start: 0,
          length: 100,
          searchValue: code,
          searchField: 'code',
          categoryValue: 0,
        };
        const encryptedPayload = encryptPayload(requestData);
        if (!encryptedPayload) {
          return { ok: true };
        }
        const fd = new FormData();
        fd.append('encrypted_payload', encryptedPayload);
        body = fd;
      } else {
        const params = new URLSearchParams();
        params.append('draw', '1');
        params.append('start', '0');
        params.append('length', '100');
        params.append('searchValue', code);
        params.append('searchField', 'code');
        params.append('search[value]', code);
        params.append('search[regex]', 'false');
        params.append('categoryValue', '0');
        duplicateCheckColumnIds.forEach((col, index) => {
          params.append(`columns[${index}][data]`, col);
          params.append(`columns[${index}][name]`, '');
          params.append(`columns[${index}][searchable]`, 'true');
          params.append(`columns[${index}][orderable]`, 'false');
          params.append(`columns[${index}][search][value]`, '');
          params.append(`columns[${index}][search][regex]`, 'false');
        });
        body = params.toString();
        headers['Content-Type'] = 'application/x-www-form-urlencoded';
      }
      const response = await fetch(`${config.API_URL}/products/getall_product`, {
        method: 'POST',
        headers,
        body,
      });
      const json = await response.json();
      const decrypted = useEncrypt ? resolveEncryptedResponse(json) : null;
      const resolved =
        useEncrypt && decrypted && typeof decrypted === 'object' ? decrypted : json;
      const rows = Array.isArray(resolved.data) ? resolved.data : [];
      const normalized = code.toLowerCase();
      const conflict = rows.find((row) => {
        const rowCode = String(row.code ?? '').trim().toLowerCase();
        if (rowCode !== normalized) {
          return false;
        }
        return String(row.id) !== String(productId);
      });
      if (conflict) {
        return {
          ok: false,
          message: 'This product code is already in use.',
        };
      }
    } catch (e) {
    }
    return { ok: true };
  };

  const submitProductForm = async (event) => {
    event.preventDefault();

    const validation = validateForm();
    if (validation !== true) {
      toast.error("Please fill the required fields");
      return;
    }
    const dupCheck = await checkDuplicateProductCodeBeforeUpdate();
    if (!dupCheck.ok) {
      setErrors((prev) => ({
        ...prev,
        code: dupCheck.message,
      }));
      toast.error(dupCheck.message);
      return;
    }
    const form = document.getElementById('updateProductForm');
    const cbm =
      formData.cbm != null && String(formData.cbm).trim() !== ''
        ? String(formData.cbm)
        : '0';
    const formDataObj = new FormData(form);
    formDataObj.delete('supplier_id[]');
    const supplierInputs = form.querySelectorAll('input[name="supplier_id[]"]');
    supplierInputs.forEach((input) => {
      formDataObj.append('supplier_id[]', input.value);
    });
    if (supplierInputs.length === 0) {
      formDataObj.append('supplier_id[]', '');
    }
    formDataObj.set('cbm', cbm);
    formDataObj.set('unit', (formData.unit != null && formData.unit !== '' ? formData.unit : selectedUnit?.value) || '');
    formDataObj.set('tax_rate', formData.vat_id || formData.tax_rate || '');
    formDataObj.set('category', (formData.category_id != null && formData.category_id !== '' ? formData.category_id : selectedCategory?.value) || '');
    formDataObj.set('brand', formData.brand || '');
    formDataObj.set("is_discount_allow", formData.is_discount_allow === "1" ? "1" : "0");
    formDataObj.set("new_arrival", formData.new_arrival === "1" ? "1" : "0");
    formDataObj.set("featured", formData.featured === "1" ? "1" : "0");
    formDataObj.set("availableFor", formData.availableFor === "1" ? "1" : "0");
    if (showCessRate === "Y") {
      formDataObj.set('cess_rate', formData.cess_id || '');
    }
    formDataObj.set('maximum_order_quantity', formData.maximum_order_quantity ?? '');
    let useEncryptUpdate = false;
    try {
      const settings = await getCompanySettings();
      useEncryptUpdate = resolveCustomerApiEncrypt(settings);
      let uploadBody;
      if (useEncryptUpdate) {
        const plain = plainObjectFromFormData(formDataObj);
        const encryptedPayload = encryptPayload(plain);
        if (!encryptedPayload) {
          toast.error("Encryption key missing.");
          return;
        }
        uploadBody = new FormData();
        uploadBody.append("encrypted_payload", encryptedPayload);
        const imgInput = document.getElementById("product_image_input");
        if (imgInput && imgInput.files && imgInput.files[0]) {
          uploadBody.append("product_image", imgInput.files[0]);
        }
      } else {
        uploadBody = formDataObj;
      }
      const response = await axios.post(`${config.API_URL}/update_product`, uploadBody, {
        headers: {
          authorization: `Bearer ${jwtToken || accessToken}`,
        }
      });
      const result = normalizeEncryptedApiResponse(response.data, useEncryptUpdate);
      const msg = result.message;
      if (result.success == true) {
        toast.success("Product updated successfully.");
        if (isAtrPrefix) {
          setQbPromptOpen(true);
        } else {
          onSuccess?.();
          setTimeout(() => {
            onClose?.();
          }, 500);
        }
      } else {
        const lower = String(msg || '').toLowerCase();
        const duplicateCode =
          lower.includes('code') &&
          (lower.includes('already') ||
            lower.includes('duplicate') ||
            lower.includes('exist') ||
            lower.includes('taken'));
        if (duplicateCode) {
          setErrors((prev) => ({
            ...prev,
            code: msg || 'This product code is already in use.',
          }));
        }
        toast.error(msg || "Failed to update product.");
      }
    } catch (error) {
      const rawErr = error.response?.data;
      const errBody = normalizeEncryptedApiResponse(rawErr, useEncryptUpdate);
      const status = error.response?.status;
      const msg = errBody?.message ?? rawErr?.message ?? '';
      const lower = String(msg).toLowerCase();
      const duplicateCode =
        status === 409 ||
        (/code|product/.test(lower) &&
          (lower.includes('already') ||
            lower.includes('duplicate') ||
            lower.includes('exist') ||
            lower.includes('taken') ||
            lower.includes('in use')));
      if (duplicateCode) {
        const codeMsg = msg || 'This product code is already in use.';
        setErrors((prev) => ({ ...prev, code: codeMsg }));
        toast.error(codeMsg);
      } else {
        toast.error(msg || "Failed to update product.");
      }
    }
  };

  const acceptNumber = (event) => {
    let value = event.target.value;
    value = value.replace(/[^0-9.]/g, "");
    if (value.indexOf('.') !== -1) {
      const parts = value.split('.');
      value = parts[0] + '.' + parts[1].substring(0, 2);
    }


    event.target.value = value;
  };


  if (!productId) return null;

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 1.5, height: '90vh', maxHeight: '90vh' } }}
    >
      <DialogContent sx={{ p: 0, overflow: 'auto', position: 'relative' }}>
        <IconButton onClick={onClose} aria-label="close" sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1, bgcolor: 'background.paper', '&:hover': { bgcolor: 'action.hover' } }}>
          <Iconify icon="eva:close-fill" width={24} />
        </IconButton>
        <Paper className="paper-style" sx={{ p: 1.5 }} elevation={0} >
          <Box>

            <Typography sx={{ fontWeight: 500, mb: 2, fontSize: '12px' }}>
              Note : Fields marked with a red asterisk (*) are mandatory
            </Typography>
          </Box>

          <form id='updateProductForm'>
            <input type="hidden" value={userId} name="user_id" />
            <input type="hidden" value={productId} name="id" />
            <Grid2 container spacing={3}>
              <Grid2 size={{ xs: 12, md: 6 }}>
                {hasDynamicFields ? (
                  <>
                    <DynamicProductFormFields
                      fields={productDetailsFields}
                      formData={dynamicFormData}
                      errors={errors}
                      onChange={handleDynamicFieldChange}
                      isReadOnly={isReadOnly}
                      optionsBySource={optionsBySource}
                    />
                    <input type="hidden" value={formData.slug} name="slug" />
                    <input type="hidden" value={formData.length_unit || "mm"} name="length_unit" />
                    <input type="hidden" value={formData.width_unit || "mm"} name="width_unit" />
                    <input type="hidden" value={formData.height_unit || "mm"} name="height_unit" />
                    {hasDynField("product_image") && (
                    <Grid2 container spacing={2} sx={{ mt: 1.2 }}>
                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" sx={{ mb: 0.2, fontWeight: 400 }}>
                          Product Image
                          {findDynField("product_image")?.required ? (
                            <span style={{ color: "red" }}> *</span>
                          ) : null}
                        </Typography>
                        <Box
                          sx={{
                            border: errors.product_image
                              ? "1px dashed #d32f2f"
                              : "1px dashed rgba(40, 41, 41, 0.64)",
                            borderRadius: 1,
                            p: 1,
                            textAlign: 'center',
                            cursor: 'pointer',
                            transition: 'border-color 0.2s',
                            '&:hover': { borderColor: 'primary.main' }
                          }}
                          onClick={() => document.getElementById('product_image_input').click()}
                        >
                          <input
                            type="file"
                            id="product_image_input"
                            name="product_image"
                            accept="image/png, image/jpeg"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                          />
                          <Iconify icon="mdi:cloud-upload-outline" width={32} sx={{ color: 'text.secondary' }} />
                          <Typography variant="body2">
                            Click to upload image
                          </Typography>
                          <Typography variant="caption">
                            PNG, JPG
                          </Typography>
                        </Box>
                        {errors.product_image ? (
                          <Typography variant="caption" sx={{ color: "error.main" }}>
                            {errors.product_image}
                          </Typography>
                        ) : null}
                        {imagePreview && (
                          <Box sx={{ mt: 2, position: "relative", width: "fit-content" }}>
                            <img
                              src={imagePreview}
                              alt="Preview"
                              style={{
                                width: 100,
                                borderRadius: 8,
                                border: "1px solid rgba(145, 158, 171, 0.16)",
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={handleRemoveImage}
                              sx={{
                                position: "absolute",
                                top: -8,
                                right: -8,
                                width: 22,
                                height: 22,
                                bgcolor: "error.main",
                                color: "#fff",
                                '&:hover': { bgcolor: "error.dark" }
                              }}
                            >
                              <Iconify icon="mdi:close" width={14} />
                            </IconButton>
                          </Box>
                        )}
                      </Grid2>
                    </Grid2>
                    )}
                  </>
                ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      maxWidth: { xs: '100%', md: '30%' },
                    }}
                  >
                    <TextField
                      className="input-border text-field"
                      fullWidth
                      label="Product Code"
                      required
                      size="small"
                      name="code"
                      value={formData.code}
                      onChange={handleChange}
                      inputProps={{ maxLength: 10, readOnly: isReadOnly }}
                      error={!!errors.code}
                      helperText={errors.code}
                      InputLabelProps={{
                        sx: {
                          "& .MuiFormLabel-asterisk": {
                            color: "red"
                          }
                        }
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 2 }}>
                    <TextField
                      className="input-border text-field"
                      label="Product Name"
                      required
                      fullWidth
                      size="small"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      error={!!errors.name}
                      helperText={errors.name}
                      InputLabelProps={{
                        sx: {
                          "& .MuiFormLabel-asterisk": {
                            color: "red"
                          }
                        }
                      }}
                    />
                  </Box>
                </Box>
                )}

                {!hasDynamicFields && !dynamicFormKeys.has("details") && (
                <Box sx={{ mb: 3 }}>
                  <TextField
                    className="input-border text-field"
                    label="Product Details"
                    fullWidth
                    size="small"
                    name="details"
                    value={formData.details}
                    onChange={handleChange}
                    multiline
                    rows={2}
                  />
                  <input type="hidden" value={formData.slug} name="slug" />
                </Box>
                )}

                {!hasDynamicFields && (!dynamicFormKeys.has("size") || !dynamicFormKeys.has("pack")) && (
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  {!dynamicFormKeys.has("size") && (
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      slotProps={{ inputLabel: { shrink: true } }}
                      className="input-border text-field"
                      label="Size"
                      required
                      fullWidth
                      size="small"
                      name="size"
                      value={formData.size}
                      onChange={handleChange}
                      error={!!errors.size}
                      helperText={errors.size}
                      InputLabelProps={{
                        sx: {
                          "& .MuiFormLabel-asterisk": {
                            color: "red"
                          }
                        }
                      }}
                    />
                  </Box>
                  )}
                  {!dynamicFormKeys.has("pack") && (
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      slotProps={{ inputLabel: { shrink: true } }}
                      className="input-border text-field"
                      label="Pack"
                      required
                      fullWidth
                      size="small"
                      type="number"
                      name="pack"
                      value={formData.pack}
                      onChange={handleChange}
                      onWheel={(e) => e.target.blur()}
                      error={!!errors.pack}
                      helperText={errors.pack}
                      InputLabelProps={{
                        sx: {
                          "& .MuiFormLabel-asterisk": {
                            color: "red !important"
                          }
                        }
                      }}
                    />
                  </Box>
                  )}
                </Box>
                )}

                {!hasDynamicFields && (
                <>
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box sx={{ flex: 1 }}>
                    <Autocomplete
                      size="small"
                      options={unitOptions}
                      value={selectedUnit}
                      onChange={handleUnitChange}
                      isOptionEqualToValue={(a, b) =>
                        a != null &&
                        b != null &&
                        String(a.value) === String(b.value)
                      }
                      getOptionLabel={(option) => option.label || ''}
                      sx={{
                        "& .MuiInputBase-input": {
                          fontSize: "13px!important"
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          slotProps={{ inputLabel: { shrink: true } }}
                          className="input-border text-field"
                          {...params}
                          label="Unit"
                          required
                          placeholder="Select Unit"
                          InputLabelProps={{
                            ...params.InputLabelProps,
                            sx: {
                              "& .MuiFormLabel-asterisk": {
                                color: "red"
                              }
                            }
                          }}
                        />
                      )}
                    />
                    {errors.unit && <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{errors.unit}</Typography>}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Autocomplete
                      slotProps={{ inputLabel: { shrink: true } }}
                      size="small"
                      options={vatOptions}
                      value={selectedVat}
                      onChange={handleVatChange}
                      isOptionEqualToValue={(a, b) =>
                        a != null &&
                        b != null &&
                        String(a.value) === String(b.value)
                      }
                      getOptionLabel={(option) => option.label || ''}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          className="input-border text-field"
                          label="VAT"
                          required
                          placeholder="Select VAT"
                          sx={{
                            "& .MuiInputBase-input": {
                              fontSize: "13px!important"
                            }
                          }}
                          InputLabelProps={{
                            ...params.InputLabelProps,
                            sx: {
                              "& .MuiFormLabel-asterisk": {
                                color: "red"
                              }
                            }
                          }}
                        />
                      )}
                    />

                    {errors.vat_id && <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{errors.vat_id}</Typography>}
                  </Box>
                </Box>

                {showCessRate === 'Y' && (
                  <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: '#637381', fontSize: '13px', mb: 0.2, fontWeight: 500 }}>CESS</Typography>
                      <Autocomplete
                        label="CESS"
                        size="small"
                        options={cessOptions}
                        value={selectedCess}
                        onChange={handleCessChange}
                        getOptionLabel={(option) => option.label || ''}
                        renderInput={(params) => (
                          <TextField className="form-input-border" {...params} placeholder="Select CESS" />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>

                      <TextField
                        className="input-border text-field"
                        label="HSN Code"
                        fullWidth
                        size="small"
                        name="hsn_code"
                        value={formData.hsn_code}
                        onChange={handleChange}
                      />
                    </Box>
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      slotProps={{ inputLabel: { shrink: true } }}
                      className="input-border text-field"
                      label="Min Qty"
                      fullWidth
                      size="small"
                      type="number"
                      name="min_alert_quantity"
                      value={formData.min_alert_quantity}
                      onChange={handleChange}
                      onWheel={(e) => e.target.blur()}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      slotProps={{ inputLabel: { shrink: true } }}
                      label="Max Qty"
                      className="input-border text-field"
                      fullWidth
                      size="small"
                      type="number"
                      name="max_alert_quantity"
                      value={formData.max_alert_quantity}
                      onChange={handleChange}
                      onWheel={(e) => e.target.blur()}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      slotProps={{ inputLabel: { shrink: true } }}
                      className="input-border text-field"
                      label="Maximum Order Qty"
                      fullWidth
                      size="small"
                      type="number"
                      name="maximum_order_quantity"
                      value={formData.maximum_order_quantity}
                      onChange={handleChange}
                      onWheel={(e) => e.target.blur()}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box sx={{ flex: 1 }}>
                    <Autocomplete
                      size="small"
                      options={brandOptions}
                      value={selectedBrand}
                      sx={{
                        "& .MuiInputBase-input": {
                          fontSize: "13px!important"
                        }
                      }}
                      onChange={handleBrandChange}
                      getOptionLabel={(option) => option.label || ''}
                      renderInput={(params) => (
                        <TextField
                          slotProps={{ inputLabel: { shrink: true } }}
                          className="input-border text-field" label="Brand"
                          {...params}
                          placeholder="Select Brand" />
                      )}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Autocomplete
                      size="small"
                      options={categoryOptions}
                      value={selectedCategory}
                      onChange={handleCategoryChange}
                      isOptionEqualToValue={(a, b) =>
                        a != null &&
                        b != null &&
                        String(a.value) === String(b.value)
                      }
                      getOptionLabel={(option) => option.label || ''}
                      sx={{
                        "& .MuiInputBase-input": {
                          fontSize: "13px!important"
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          className="input-border text-field"
                          label="Category"
                          required
                          slotProps={{ inputLabel: { shrink: true } }}
                          InputLabelProps={{
                            ...params.InputLabelProps,
                            sx: {
                              "& .MuiFormLabel-asterisk": {
                                color: "red"
                              }
                            }
                          }}
                          placeholder="Select Category" />
                      )}
                    />
                    {errors.category_id && <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{errors.category_id}</Typography>}
                  </Box>
                </Box>
                {/* ================= Dimensions ================= */}
                <Box sx={{ mb: 3 }}>
                  <Grid2 container spacing={2} sx={{ mb: 2 }}>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <Box sx={{ display: "flex", gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <TextField
                          label="Length"
                          className="input-border text-field"
                          size="small"
                          name="length"
                          id="add_length"
                          value={formData.length}
                          onChange={(e) => { acceptNumber(e); handleChange(e); }}
                          sx={{ flex: 1 }}
                          fullWidth
                          slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 75 } }}>
                          <Select
                            className="input-border text-field"
                            name="length_unit"
                            id="length_unit"
                            value={formData.length_unit || 'mm'}
                            onChange={(e) => { handleChange(e); }}
                            sx={{ fontSize: 13, height:35}}
                          >
                            <MenuItem value="mm">MM</MenuItem>
                            <MenuItem value="cm">CM</MenuItem>
                            <MenuItem value="in">IN</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <Box sx={{ display: "flex", gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <TextField
                          label="Width"
                          className="input-border text-field"
                          size="small"
                          name="width"
                          id="add_width"
                          value={formData.width}
                          onChange={(e) => { acceptNumber(e); handleChange(e); }}
                          sx={{ flex: 1 }}
                          fullWidth
                          slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 75 } }}>
                          <Select
                            className="input-border text-field"
                            name="width_unit"
                            id="width_unit"
                            value={formData.width_unit || 'mm'}
                            onChange={(e) => { handleChange(e); }}
                            sx={{ fontSize: 13, height:35}}
                          >
                            <MenuItem value="mm">MM</MenuItem>
                            <MenuItem value="cm">CM</MenuItem>
                            <MenuItem value="in">IN</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </Grid2>
                  </Grid2>
                  <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <Box sx={{ display: "flex", gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <TextField
                          className="input-border text-field"
                          label="Height"
                          size="small"
                          name="height"
                          id="add_height"
                          value={formData.height}
                          onChange={(e) => { acceptNumber(e); handleChange(e); }}
                          sx={{ flex: 1,
                    
                           }}
                          fullWidth
                          slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <FormControl sx={{ minWidth: { xs: '100%', sm: 75 } }}>
                          <Select
                            className="input-border text-field"
                            size="small"
                            name="height_unit"
                            id="height_unit"
                            value={formData.height_unit || 'mm'}
                            onChange={(e) => { handleChange(e); }}
                            sx={{ fontSize: 13, height:35}}
                          >
                            <MenuItem value="mm">MM</MenuItem>
                            <MenuItem value="cm">CM</MenuItem>
                            <MenuItem value="in">IN</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </Grid2>
                  </Grid2>
                </Box>

                {/* ================= CBM & Weight ================= */}
                <Grid2 container spacing={2} sx={{ mb: 2 }}>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <TextField
                     slotProps={{ inputLabel: { shrink: true } }}
                     className="input-border text-field" label="CBM" fullWidth size="small" name="cbm" id="add_cbm" value={formData.cbm} disabled />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <TextField
                     slotProps={{ inputLabel: { shrink: true } }}
                     className="input-border text-field" fullWidth size="small" label="Gross Weight" name="gross_weight" value={formData.gross_weight} onChange={(e) => { acceptNumber(e); handleChange(e); }} placeholder="kg" />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <TextField
                     slotProps={{ inputLabel: { shrink: true } }}
                     className="input-border text-field" fullWidth size="small" label="Net Weight" name="net_weight" value={formData.net_weight} onChange={(e) => { acceptNumber(e); handleChange(e); }} placeholder="kg" />
                  </Grid2>
                </Grid2>

                {/* ================= Bay & Rack ================= */}
                <Grid2 container spacing={2} sx={{ mb: 2 }}>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <TextField
                     slotProps={{ inputLabel: { shrink: true } }}
                     className="input-border text-field" label="Bay" fullWidth size="small" name="bay" value={formData.bay} onChange={handleChange} />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <TextField
                     slotProps={{ inputLabel: { shrink: true } }}
                     className="input-border text-field" label="Rack" fullWidth size="small" name="rack" value={formData.rack} onChange={handleChange} />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <TextField
                     slotProps={{ inputLabel: { shrink: true } }}
                     className="input-border text-field" label="Part Number" fullWidth size="small" name="part_number" value={formData.part_number} onChange={handleChange} />
                  </Grid2>
                </Grid2>

                {/* ================= EAN Codes ================= */}
                <Grid2 container spacing={2} sx={{ mb: 2 }}>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <TextField
                     slotProps={{ inputLabel: { shrink: true } }}
                     className="input-border text-field" label="Inner EAN" fullWidth size="small" name="inner_ean_number" value={formData.inner_ean_number} onChange={handleChange} />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <TextField
                     slotProps={{ inputLabel: { shrink: true } }}
                     className="input-border text-field" label="Outer EAN" fullWidth size="small" name="outer_ean_number" value={formData.outer_ean_number} onChange={handleChange} />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <TextField
                     slotProps={{ inputLabel: { shrink: true } }}
                     className="input-border text-field" label="Pallet EAN" fullWidth size="small" name="pallete_ean" value={formData.pallete_ean} onChange={handleChange} />
                  </Grid2>
                </Grid2>

                {/* ================= Flags ================= */}
                <Grid2 container spacing={2} sx={{ mb: 2 }}>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          className="checkBox"
                          size="small"
                          name="new_arrival"
                          checked={formData.new_arrival === "1"}
                          onChange={handleCheckboxChange}
                        />
                      }
                      label="New Arrival"
                 
                    />
                    {formData.new_arrival === "1" && (
                      <Box sx={{ mt: 0.5 }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            className="form-input-border"
                            label="Arrival Ends On"
                            value={formData.new_arrival_date ? new Date(formData.new_arrival_date) : null}
                            onChange={(newValue) => {
                              const dateStr = newValue ? format(newValue, 'yyyy-MM-dd') : '';
                              setFormData((prevData) => ({
                                ...prevData,
                                new_arrival_date: dateStr,
                              }));
                            }}
                            slotProps={{
                              textField: {
                                size: 'small',
                                fullWidth: true,
                                name: 'new_arrival_date',
                              }
                            }}
                          />
                        </LocalizationProvider>
                      </Box>
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          className="checkBox"
                          size="small"
                          name="is_discount_allow"
                          checked={formData.is_discount_allow === "1"}
                          onChange={handleCheckboxChange}
                        />
                      }
                      label="Discount Product"
                    />
                    {formData.is_discount_allow === "1" && (
                      <Box sx={{ mt: 0.5 }}>
                        <TextField
                          className="form-input-border"
                          label="Discount %"
                          fullWidth
                          size="small"
                          type="number"
                          name="percentage"
                          value={formData.percentage}
                          onChange={handleChange}
                          onWheel={(e) => e.target.blur()}
                        />
                      </Box>
                    )}
                  </Grid2>
                </Grid2>
                {/* ================= Image & Featured ================= */}
                <Grid2 container spacing={2} sx={{ mt: 1.2 }}>
                  <Grid2 size={6}>
                    <Typography variant="body2" sx={{ mb: 0.2, fontWeight: 400 }}>
                      Product Image
                    </Typography>
                    <Box
                      sx={{
                        border: '1px dashed rgba(40, 41, 41, 0.64)',
                        borderRadius: 1,
                        p: 1,
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'border-color 0.2s',
                        '&:hover': { borderColor: 'primary.main' }
                      }}
                      onClick={() => document.getElementById('product_image_input').click()}
                    >
                      <input
                        type="file"
                        id="product_image_input"
                        name="product_image"
                        accept="image/png, image/jpeg"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                      />
                      <Iconify icon="mdi:cloud-upload-outline" width={32} sx={{ color: 'text.secondary' }} />
                      <Typography variant="body2">
                        Click to upload image
                      </Typography>
                      <Typography variant="caption">
                        PNG, JPG
                      </Typography>
                    </Box>

                    {imagePreview && (
                      <Box sx={{ mt: 2, position: "relative", width: "fit-content" }}>
                        <img
                          src={imagePreview}
                          alt="Preview"
                          style={{
                            width: 100,
                            borderRadius: 8,
                            border: "1px solid rgba(145, 158, 171, 0.16)",
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={handleRemoveImage}
                          sx={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            width: 22,
                            height: 22,
                            bgcolor: "error.main",
                            color: "#fff",
                            '&:hover': { bgcolor: "error.dark" }
                          }}
                        >
                          <Iconify icon="mdi:close" width={14} />
                        </IconButton>
                      </Box>
                    )}
                  </Grid2>

                  <Grid2 size={6} display="flex" gap={1} flexDirection="column">
                    <Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            className="checkBox"
                            size="small"
                            name="featured"
                            checked={formData.featured === "1"}
                            onChange={handleCheckboxChange}
                          />
                        }
                        label="Featured (Shop homepage listing)"
                      />
                    </Box>
                    <Box>
                      <Typography className="productForm-lables">Available For</Typography>
                      <Select
                        className="input-border"
                        fullWidth
                        placeholder="Select Available For"
                        size="small"
                        name="availableFor"
                        value={formData.availableFor === "1" ? "1" : "0"}
                        onChange={handleChange}
                      >
                        <MenuItem value="0">All</MenuItem>
                        <MenuItem value="1">TeleCaller</MenuItem>
                      </Select>
                    </Box>
                  </Grid2>
                </Grid2>
                </>
                )}
              </Grid2>

              <Grid2 size={{ xs: 12, md: 6 }}>
                {(!hasDynamicFields || hasDynField("product_type")) && (
                <Box sx={{ mb: 1 }}>
                  <FormControl>
                    <RadioGroup
                      row
                      name="split"
                      value={selectedOption}
                      onChange={(e) => toggleOptions(e.target.value)}
                    >
                      <FormControlLabel value="0" control={<Radio size="small" className="checkBox" />} label="Box" />
                      <FormControlLabel value="1" control={<Radio size="small" className="checkBox" />} label="Piece" />
                      <FormControlLabel value="2" control={<Radio size="small" className="checkBox" />} label="Both" />
                    </RadioGroup>
                  </FormControl>
                </Box>
                )}

                {showBoxPricing && (
                  <Paper variant="outlined" sx={{ px: 2, py: 1, borderRadius: 1, borderColor: 'rgba(59, 60, 61, 0.2)', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, pb: 0.2, borderBottom: '1px solid rgba(61, 62, 63, 0.28)', fontWeight: 600, fontSize: '14px' }}>
                      Box Pricing
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                      {(!hasDynamicFields || hasDynField("box_cost") || hasDynField("cost")) &&
                        (!useDynamicForm || activeBoxSubFields?.box_cost !== false) && (
                      <Box sx={{ flex: 1 }}>
                        <TextField
                          slotProps={{ inputLabel: { shrink: true } }}
                          className="input-border text-field"
                          label="Box Cost"
                          required
                          fullWidth
                          size="small"
                          type="number"
                          name="cost"
                          value={formData.cost}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          onWheel={(e) => e.target.blur()}
                          error={!!errors.cost}
                          helperText={errors.cost}
                          InputLabelProps={{
                            sx: {
                              "& .MuiFormLabel-asterisk": {
                                color: "red"
                              }
                            }
                          }}
                        />
                      </Box>
                      )}
                      {(!hasDynamicFields || hasDynField("recommended_selling_price") || hasDynField("recommendedSellingBoxPrice")) &&
                        (!useDynamicForm || activeBoxSubFields?.recommended_selling_price !== false) && (
                      <Box sx={{ flex: 1 }}>
                        <TextField
                          slotProps={{ inputLabel: { shrink: true } }}
                          className="input-border text-field"
                          label="Recommended Selling Price"
                          required
                          fullWidth
                          size="small"
                          type="number"
                          name="recommendedSellingBoxPrice"
                          value={formData.recommendedSellingBoxPrice}
                          onChange={handleChange}
                          onWheel={(e) => e.target.blur()}
                          error={!!errors.recommendedSellingBoxPrice}
                          helperText={errors.recommendedSellingBoxPrice}
                          InputLabelProps={{
                            sx: {
                              "& .MuiFormLabel-asterisk": {
                                color: "red"
                              }
                            }
                          }}
                        />
                      </Box>
                      )}
                    </Box>
                    <input type="hidden" name="old_box_cost" value={formData.old_box_cost} />

                    {(!hasDynamicFields ||
                      hasDynField("box_prices") ||
                      hasDynField("price_box") ||
                      hasDynField("special_price_box")) && (
                    <BoxPricingTable
                      formData={formData}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      errors={errors}
                      selectedOption={selectedOption}
                      accessToken={accessToken}
                      visibleSubFields={useDynamicForm ? activeBoxSubFields : null}
                    />
                    )}

                    {(!useDynamicForm || activeBoxSubFields?.box_margin !== false) && (
                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                      <Box sx={{ flex: 1, maxWidth: '50%' }}>
                        <Typography sx={{fontSize: '13px', mb: 0.5, fontWeight: 500 }}>Box Margin(%)</Typography>
                        <TextField
                          className="input-border text-field"
                          fullWidth
                          size="small"
                          type="number"
                          name="margin"
                          value={formData.margin}
                          onChange={handleChange}
                          onWheel={(e) => e.target.blur()}
                          InputProps={{ readOnly: true }}
                          error={!!errors.margin}
                          helperText={errors.margin}
                        />
                      </Box>
                    </Box>
                    )}
                  </Paper>
                )}

                {showPiecePricing && (
                  <Paper variant="outlined" sx={{ px: 2, py: 1, borderRadius: 1, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, pb: 0.5, borderBottom: '1px solid rgba(145, 158, 171, 0.16)', fontWeight: 600, fontSize: '14px' }}>
                      Piece Pricing
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                      {(!hasDynamicFields || hasDynField("piece_cost")) &&
                        (!useDynamicForm || activePieceSubFields?.piece_cost !== false) && (
                      <Box sx={{ flex: 1 }}>
                        <TextField
                          className="input-border text-field"
                          label="Piece Cost"
                          required
                          fullWidth
                          size="small"
                          type="number"
                          name="piece_cost"
                          value={formData.piece_cost}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          onWheel={(e) => e.target.blur()}
                          error={!!errors.piece_cost}
                          helperText={errors.piece_cost}
                          InputLabelProps={{
                            sx: {
                              "& .MuiFormLabel-asterisk": {
                                color: "red"
                              }
                            }
                          }}
                        />
                      </Box>
                      )}
                      {(!hasDynamicFields || hasDynField("piece_recommended_selling_price") || hasDynField("recommendedSellingPiecePrice")) &&
                        (!useDynamicForm || activePieceSubFields?.piece_recommended_selling_price !== false) && (
                      <Box sx={{ flex: 1 }}>
                        <TextField
                          className="input-border text-field"
                          fullWidth
                          label="Recommended Selling Price (Piece)"
                          required
                          size="small"
                          type="number"
                          name="recommendedSellingPiecePrice"
                          value={formData.recommendedSellingPiecePrice}
                          onChange={handleChange}
                          onWheel={(e) => e.target.blur()}
                          error={!!errors.recommendedSellingPiecePrice}
                          helperText={errors.recommendedSellingPiecePrice}
                          InputLabelProps={{
                            sx: {
                              "& .MuiFormLabel-asterisk": {
                                color: "red"
                              }
                            }
                          }}
                        />
                      </Box>
                      )}
                    </Box>
                    <input type="hidden" name="old_piece_cost" value={formData.old_piece_cost} />
                    <input type="hidden" name="old_price" value={formData.old_price} />

                    {(!hasDynamicFields ||
                      hasDynField("piece_prices") ||
                      hasDynField("price_piece") ||
                      hasDynField("special_price_piece")) &&
                      selectedOption == "1" && (
                      <PiecePricingTable
                        formData={formData}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        errors={errors}
                        accessToken={accessToken}
                        visibleSubFields={useDynamicForm ? activePieceSubFields : null}
                      />
                    )}

                    {(!useDynamicForm || activePieceSubFields?.piece_margin !== false) && (
                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                      <Box sx={{ flex: 1, maxWidth: '50%' }}>
                        <Typography variant="body2" sx={{ mb: 0.5, fontSize: '13px' }}>Piece Margin(%)</Typography>
                        <TextField
                          className="form-input-border"
                          fullWidth
                          size="small"
                          type="number"
                          name="piece_margin"
                          value={formData.piece_margin}
                          onChange={handleChange}
                          onWheel={(e) => e.target.blur()}
                          InputProps={{ readOnly: true }}
                          error={!!errors.piece_margin}
                          helperText={errors.piece_margin}
                        />
                      </Box>
                    </Box>
                    )}
                  </Paper>
                )}

                {showSelectSupplier && (
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ fontSize: '13px', mb: 0.2, fontWeight: 500 }}>Select Supplier</Typography>
                  <Box
                    sx={{
                      maxHeight: 220,
                      overflowY: "auto",
                      border: "1px solid rgba(59, 60, 61, 0.2)",
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    <SupplierSuggestionList supplierArray={supplierList} key={String(productId)} />
                  </Box>
                </Box>
                )}
              </Grid2>
            </Grid2>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                className="button-variant-contained"
                onClick={submitProductForm}

              >
                Update Product
              </Button>
            </Box>
          </form>
        </Paper>
      </DialogContent>
    </Dialog>
    <QuickBooksPushPrompt
      open={qbPromptOpen}
      pushPath="products"
      onDone={() => {
        setQbPromptOpen(false);
        onSuccess?.();
        onClose?.();
      }}
    />
    </>
  );
};

export default Edit_Product;
